/**
 * Pure helpers for the transcript-upload word-count cap.
 *
 * Kept separate from ``TranscriptWordLimitModal.jsx`` for two reasons:
 *
 *   1. React-refresh's "only export components" ESLint rule fires when a
 *      component file also exports plain values / helpers.
 *   2. These functions are pure JS with no MUI or React imports, which
 *      lets them be tested by ``node --test`` without needing a full
 *      vitest + jsdom setup for the repo. The React component itself
 *      still has no coverage until we add that setup as a follow-up.
 *
 * Wire contract with customer-api (do NOT rename these strings without
 * the paired backend change): the 413 body from
 * ``app/services/transcript_word_limit.py`` carries::
 *
 *   {
 *     error_code: "TRANSCRIPT_WORD_LIMIT_EXCEEDED",
 *     message: "…",
 *     limit: 100000,
 *     over_limit_files: [{ filename, word_count }, …],
 *     contact_url: "https://qualz.ai/contactus",
 *   }
 */

export const TRANSCRIPT_WORD_LIMIT_ERROR_CODE = 'TRANSCRIPT_WORD_LIMIT_EXCEEDED';
export const TRANSCRIPT_WORD_LIMIT = 100_000;
export const TRANSCRIPT_WORD_LIMIT_CONTACT_URL = 'https://qualz.ai/contactus';

/**
 * Whitespace-tokenise. Precise enough for the 100k threshold — the same
 * function the backend uses in ``transcript_word_limit._count_words``.
 * @param {string} text
 * @returns {number}
 */
export function countWordsInText(text) {
  if (typeof text !== 'string') return 0;
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

/**
 * True when the axios error carries our structured 413 payload. Used to
 * choose between the word-limit modal and the generic "upload failed"
 * toast in FileUpload's catch blocks.
 * @param {any} err
 * @returns {boolean}
 */
export function isTranscriptWordLimitError(err) {
  if (err?.response?.status !== 413) return false;
  const detail = err?.response?.data?.detail;
  return Boolean(detail && detail.error_code === TRANSCRIPT_WORD_LIMIT_ERROR_CODE);
}

/**
 * Normalise the 413 body into the shape the modal renders. Also used as
 * the shape of the client-side pre-check result so both paths converge
 * on one prop contract.
 * @param {any} err
 * @returns {{limit: number, overLimitFiles: Array<{filename: string, wordCount: number|null}>, contactUrl: string}}
 */
export function extractTranscriptWordLimitDetail(err) {
  const detail = err?.response?.data?.detail || {};
  return {
    limit: typeof detail.limit === 'number' ? detail.limit : TRANSCRIPT_WORD_LIMIT,
    overLimitFiles: Array.isArray(detail.over_limit_files)
      ? detail.over_limit_files.map((f) => ({
          filename: f?.filename || 'file',
          wordCount: typeof f?.word_count === 'number' ? f.word_count : null,
        }))
      : [],
    contactUrl:
      typeof detail.contact_url === 'string'
        ? detail.contact_url
        : TRANSCRIPT_WORD_LIMIT_CONTACT_URL,
  };
}
